import type { PartySocketOptions } from "partysocket";
import type { usePartySocket } from "partysocket/react";

import { useStablePartySocket } from "./useStablePartySocket";

declare const __API_URL__: string;

const getPartySocketUrl = () => {
  // Convert API URL to WebSocket URL
  const apiUrl = new URL(__API_URL__);
  return {
    host: apiUrl.host,
    protocol: apiUrl.protocol === "https:" ? "wss" : "ws",
  };
};

type UsePartySocketOptions = Parameters<typeof usePartySocket>[0];

export function useAppPartySocket(options: UsePartySocketOptions) {
  const { host, protocol } = getPartySocketUrl();

  // Add detailed logging to help debug connection issues
  console.log("[PartySocket] Connection parameters:", {
    host,
    protocol,
    room: options.room,
    party: options.party,
    connectionUrl: `${protocol}://${host}/${
      options.party ? `parties/${options.party}/` : ""
    }${options.room || ""}`,
  });

  // Ensure the room is properly set
  if (!options.room) {
    console.error("[PartySocket] No room specified for PartySocket");
  }

  // If a party is specified but no room, we need to handle the error case
  if (options.party && !options.room) {
    console.error("[PartySocket] Both party and room must be specified");
    throw new Error(
      "The room and host must be set before connecting, use `updateProperties` method to set them or pass them to the constructor.",
    );
  }

  // Create actual socket connection
  const socket = useStablePartySocket({
    host,
    protocol: protocol as PartySocketOptions["protocol"],
    ...options,
  });

  // Log that the socket was created successfully
  console.log("[PartySocket] Socket initialized successfully");

  return socket;
}
